import React, { Fragment, ReactElement, useEffect, useMemo } from 'react'
import { DigitalTwinBuyStep, DigitalTwinViewProps } from './DigitalTwin.types'
import Styles from './DigitalTwin.module.scss'
import Container from 'openspace/components/Container'
import ProgressBar from '@onepercentio/one-ui/components/ProgressBar'
import Spacing from '@onepercentio/one-ui/components/Spacing'
import UncontrolledTransition from '@onepercentio/one-ui/components/UncontrolledTransition'
import { Address } from 'features/Form/AddressForm/AddressForm.types'
import CheckBox from '@onepercentio/one-ui/components/CheckBox'
import { ReactComponent as IconPix } from 'assets/icons/icon_pix.svg'
import Text from 'openspace/components/Text'
import { LPTypo } from 'containers/OneUIProvider/OneUIProvider.data'
import { currencyFormatterFactory } from 'core/helpers/formatter'
import AlertContainer from 'openspace/components/AlertContainer'
import { i18n, t, tO } from 'translate/i18n'
import { AssetType, Sale } from 'core/logic/asset/asset.types'
import {
  AddressContent,
  ContinuableAction,
} from 'pages/Authenticated/Marketplace/CreateOffer/OfferType/DigitalTwin/DigitalTwin.view'
import { Continuable } from 'pages/Authenticated/Marketplace/CreateOffer/OfferType/DigitalTwin/DigitalTwin.types'
import { extractEnumValues } from 'utility/enum'
import { DeliveryQuotation } from '@onepercentio/rarum-types/logistics'
import useAsyncControl from '@onepercentio/one-ui/hooks/useAsyncControl'
import AsyncWrapper from '@onepercentio/one-ui/components/AsyncWrapper'
import CreateOfferStyles from 'pages/Authenticated/Marketplace/CreateOffer/OfferType/DigitalTwin/DigitalTwin.module.scss'
import Loader from '@onepercentio/one-ui/components/Loader'
import Radio from '@onepercentio/one-ui/components/Radio'
import { DigitalTwinBuyTestIds } from './DigitalTwin.e2e'
import AnimatedEntrance from '@onepercentio/one-ui/components/AnimatedEntrance'
import BigNumber from 'bignumber.js'
import AsyncControl from 'components/AsyncControl'

export function DeliveryMethod({
  method,
  onSetMethod,
  deliveryAddress,
  loadQuotes,
  onBack,
  availableMethods,
  ...props
}: SettableStep<'method', DeliveryQuotation> & {
  availableMethods: DeliveryQuotation[] | undefined
  deliveryAddress: Address
  sale: Sale
} & {
  loadQuotes: () => void
  onBack: () => void
} & Continuable) {
  const formatter = useMemo(() => currencyFormatterFactory('BRL'), [])
  const control = useAsyncControl({ loadQuotes })
  useEffect(() => {
    control.loadQuotes()
  }, [])

  const allQuotes = useMemo(() => {
    if (availableMethods)
      return availableMethods.reduce(
        (arr, quote) => [...arr, quote],
        [] as DeliveryQuotation[]
      )
  }, [availableMethods])
  return (
    <>
      <Text
        code='marketplace.buyOffer.digitalTwin.deliveryMethod.type'
        type={LPTypo.H4}
      />
      <Spacing size='16' />
      <Text
        code='marketplace.buyOffer.digitalTwin.deliveryMethod.select'
        type={LPTypo.P1}
      />
      <Spacing size='16' />

      <AsyncControl
        control={control}
        onClose={onBack}
        onRetry={control.loadQuotes}>
        <div className={Styles.quotesList}>
          <AnimatedEntrance>
            {allQuotes
              ? allQuotes.map((quote, idx) => (
                  <Radio
                    key={quote.id}
                    data-testid={DigitalTwinBuyTestIds.QUOTE_OPTION}
                    className={CreateOfferStyles.address}
                    groupId='address'
                    label={
                      <>
                        <div>
                          <Text type={LPTypo.H6}>
                            {t(
                              `logistics.deliveryMethod.${
                                quote.type as '03158'
                              }`
                            )}
                          </Text>
                          <Spacing size='8' />
                          <Text
                            type={LPTypo.P1}
                            code='marketplace.buyOffer.digitalTwin.deliveryMethod.price'
                            format={{
                              price: formatter.format(quote.price),
                              cep: deliveryAddress.zipCode,
                            }}
                          />
                        </div>
                      </>
                    }
                    value={quote.id!}
                    onToggle={() => {
                      onSetMethod(quote)
                    }}
                    checked={method === quote}
                  />
                ))
              : []}
          </AnimatedEntrance>
        </div>
      </AsyncControl>
      <Spacing size='8' />
      <AlertContainer
        type='warning'
        title={t(
          'marketplace.buyOffer.digitalTwin.deliveryMethod.disclaimer.title'
        )}>
        <Text
          type={LPTypo.P2}
          code='marketplace.buyOffer.digitalTwin.deliveryMethod.disclaimer.description'
        />
      </AlertContainer>
      <Spacing size='8' />
      <ContinuableAction {...props} />
    </>
  )
}

export function PaymentMethod({
  price,
  asset,
  deliveryAddress,
  quote,
  ...props
}: {
  price: number
  asset: AssetType
  deliveryAddress: Address
  quote: DeliveryQuotation
} & Continuable) {
  const name = asset[i18n.language].name
  const finalPrice = useMemo(() => {
    return BigNumber(price).plus(BigNumber(quote.price)).toNumber()
  }, [price, quote.price])
  return (
    <>
      <Text type={LPTypo.H5}>
        {t('marketplace.buyOffer.digitalTwin.resume.paymentMethod.title')}
      </Text>
      <Spacing size='16' />
      <div className={Styles.methodContainer}>
        <CheckBox
          className={Styles.method}
          checked
          groupId='methods'
          onToggle={() => {}}
          value=''
          label={
            <>
              <IconPix /> <Text type={LPTypo.H5}>PIX</Text>{' '}
              <Spacing size='stretch' />
              <span>{currencyFormatterFactory('brl').format(finalPrice)}</span>
            </>
          }
        />
        <Spacing size='32' />
        <AlertContainer
          type='warning'
          title={t(
            'marketplace.buyOffer.digitalTwin.resume.paymentMethod.alert'
          )}>
          {t(
            'marketplace.buyOffer.digitalTwin.resume.paymentMethod.disclaimer'
          )}
        </AlertContainer>
      </div>
      <Spacing size='16' />
      <Text type={LPTypo.H5}>
        {t('marketplace.buyOffer.digitalTwin.resume.title')}
      </Text>
      <Spacing size='16' />
      <div className={Styles.resume}>
        <div>
          <img src={asset.media.imageUrl} alt={name} />{' '}
          <Text type={LPTypo.H6}>{name}</Text>
        </div>
        <hr />
        <div>
          <IconPix /> <Text type={LPTypo.H5}>PIX</Text>{' '}
          <Spacing size='stretch' />
          <span>{currencyFormatterFactory('brl').format(finalPrice)}</span>
        </div>
        <hr />
        <AddressContent address={deliveryAddress} />
      </div>
      <Spacing size='stretch' />
      <ContinuableAction {...props} />
    </>
  )
}

/**
 * Buys an offer od type digital twin
 **/
export default function DigitalTwinView({
  currentStep,
  children,
}: DigitalTwinViewProps) {
  const progress = useMemo(() => {
    const vals = extractEnumValues(DigitalTwinBuyStep)
    return (100 / vals.length) * (currentStep + 1)
  }, [currentStep])
  return (
    <Container className={Styles.root}>
      <Spacing size='32' />
      <ProgressBar progress={progress} size={16} mode='gauge' />
      <Spacing size='40' />
      <UncontrolledTransition className={Styles.stretch}>
        <Fragment key={currentStep}>{children as ReactElement}</Fragment>
      </UncontrolledTransition>
    </Container>
  )
}
